/**
 * ManagerVoidDialog - Void/reverse a closed (completed+paid) sale.
 * Requires a manager's 6-digit PIN (manager must have pos.void_order).
 */
import { useState, useEffect } from "react";
import { toast } from "sonner";
import { ShieldAlert, Loader2, RotateCcw } from "lucide-react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuthStore } from "@/store/useStore";

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function ManagerVoidDialog({ open, onOpenChange, order, onDone }) {
  const { token } = useAuthStore();
  const [pin, setPin] = useState("");
  const [reason, setReason] = useState("Sale reversed");
  const [processing, setProcessing] = useState(false);

  useEffect(() => {
    if (open) {
      setPin("");
      setReason("Sale reversed");
    }
  }, [open]);

  const formatCurrency = (amount) =>
    new Intl.NumberFormat("en-KE", { style: "currency", currency: "KES", minimumFractionDigits: 0 }).format(amount || 0);

  const handleVoid = async () => {
    if (!order) return;
    if (pin.length !== 6 || !/^\d{6}$/.test(pin)) {
      toast.error("Enter the manager's 6-digit PIN");
      return;
    }
    try {
      setProcessing(true);
      const response = await fetch(`${API_URL}/api/orders/${order.id}/void-reverse`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ manager_pin: pin, reason }),
      });
      const data = await response.json();
      if (response.ok) {
        toast.success(`${data.message} · Authorized by ${data.authorized_by}`);
        onOpenChange(false);
        if (onDone) onDone(data);
      } else {
        toast.error(data.detail || "Failed to void the sale");
      }
    } catch (e) {
      toast.error("Failed to void the sale");
    } finally {
      setProcessing(false);
    }
  };

  if (!order) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-sm" data-testid="manager-void-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ShieldAlert className="h-5 w-5 text-red-600" />
            Void / Reverse Sale
          </DialogTitle>
          <DialogDescription>
            Reversing {order.invoice_number || order.order_number} ({formatCurrency(order.total)}).
            A manager must authorize this.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="space-y-2">
            <Label>Manager PIN</Label>
            <Input
              type="password"
              inputMode="numeric"
              maxLength={6}
              autoFocus
              value={pin}
              onChange={(e) => setPin(e.target.value.replace(/\D/g, "").slice(0, 6))}
              onKeyDown={(e) => e.key === "Enter" && handleVoid()}
              placeholder="● ● ● ● ● ●"
              className="h-12 text-center text-2xl tracking-[0.5em]"
              data-testid="manager-void-pin-input"
            />
          </div>
          <div className="space-y-2">
            <Label>Reason</Label>
            <Input value={reason} onChange={(e) => setReason(e.target.value)} data-testid="manager-void-reason-input" />
          </div>
          <div className="rounded-md bg-red-50 border border-red-200 p-3 text-sm text-red-700">
            This fully reverses the sale, returns all items to stock, and raises a credit note.
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={processing}>Cancel</Button>
          <Button
            onClick={handleVoid}
            disabled={processing || pin.length !== 6}
            className="bg-red-600 hover:bg-red-700"
            data-testid="manager-void-confirm-btn"
          >
            {processing ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <RotateCcw className="h-4 w-4 mr-2" />}
            Void Sale
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
